import { GETTING_STARTED_STEPS, GettingStartedData, GettingStartedStep } from "./gettingStartedSteps";

export interface GettingStartedProgress {
  completedSteps: GettingStartedStep[];
  completedCount: number;
  totalCount: number;
  progressPercentage: number;
  nextStep: GettingStartedStep | undefined;
  isComplete: boolean;
  enabledSteps: GettingStartedStep[];
}

/**
 * Calculate progress and status for all getting started steps
 */
export const calculateGettingStartedProgress = (data: GettingStartedData): GettingStartedProgress => {
  const stepsWithStatus = GETTING_STARTED_STEPS.map(step => ({
    ...step,
    completed: step.getCompleted(data),
    enabled: step.getEnabled(data),
  }));

  const completedSteps = stepsWithStatus.filter(step => step.completed);
  const enabledSteps = stepsWithStatus.filter(step => step.enabled);
  const nextStep = stepsWithStatus.find(step => !step.completed && step.enabled);

  const completedCount = completedSteps.length;
  const totalCount = GETTING_STARTED_STEPS.length;
  const progressPercentage = (completedCount / totalCount) * 100;
  const isComplete = progressPercentage === 100;

  return {
    completedSteps,
    completedCount,
    totalCount,
    progressPercentage,
    nextStep,
    isComplete,
    enabledSteps,
  };
};

/**
 * Get all steps with their current status
 */
export const getStepsWithStatus = (data: GettingStartedData) => {
  return GETTING_STARTED_STEPS.map(step => ({
    ...step,
    completed: step.getCompleted(data),
    enabled: step.getEnabled(data),
  }));
};

/**
 * Check if a specific step is the next step to complete
 */
export const isNextStep = (stepId: number, data: GettingStartedData): boolean => {
  const progress = calculateGettingStartedProgress(data);
  return progress.nextStep?.id === stepId;
};

/**
 * Get a summary string for progress
 */
export const getProgressSummary = (data: GettingStartedData): string => {
  const progress = calculateGettingStartedProgress(data);
  
  if (progress.isComplete) {
    return "Setup Complete!";
  }
  
  if (progress.nextStep) {
    return `Next: ${progress.nextStep.shortTitle}`;
  }
  
  return "Continue your setup";
};

/**
 * Check if the getting started guide should be shown
 * (i.e., not complete and has enabled steps)
 */
export const shouldShowGettingStarted = (data: GettingStartedData): boolean => {
  const progress = calculateGettingStartedProgress(data);
  return !progress.isComplete && progress.enabledSteps.length > 0;
}; 